
import { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";

interface Proverb {
  yoruba: string;
  english: string;
}

const proverbs: Proverb[] = [
  {
    yoruba: "Àgbájọ ọwọ́ la fi ń sọ́yà",
    english: "It's with combined hands that we fold a garment."
  },
  {
    yoruba: "Ọmọ tí a kò kọ́, ni yóò ta ilé tí a kọ́",
    english: "The child we don't train will sell the house we built."
  },
  {
    yoruba: "Bí ọ̀kọ̀ kan ò dé ibi tó dé, kò lè mọ ibi tí ọ̀kọ̀ kejì ń sùn",
    english: "If one boat doesn't reach its destination, it can't know where the other boat anchors."
  },
  {
    yoruba: "Àìfàní kọ́ èrò mìíràn",
    english: "Lack of opportunity teaches another thought."
  },
  {
    yoruba: "Bí a bá pẹ́ lórí omi, a máa bá àwọn ẹja",
    english: "If we stay long on the water, we'll encounter fish."
  },
  {
    yoruba: "Ẹni tí ò bá fẹ́ sun, jókòó ìjókòó àti gùn",
    english: "One who doesn't want to fall asleep should not sit in a snooze-inducing position."
  }
];

interface ProverbWidgetProps {
  className?: string;
  autoChange?: boolean;
  interval?: number;
}

const ProverbWidget = ({ 
  className, 
  autoChange = true, 
  interval = 15000 
}: ProverbWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentProverb, setCurrentProverb] = useState<Proverb>(proverbs[0]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (autoChange && isOpen) {
      const timer = setInterval(() => {
        const nextIndex = (index + 1) % proverbs.length;
        setIndex(nextIndex);
        setCurrentProverb(proverbs[nextIndex]);
      }, interval);
      
      return () => clearInterval(timer);
    }
  }, [autoChange, interval, isOpen, index]);

  const toggleWidget = () => {
    setIsOpen(!isOpen);
  };

  const nextProverb = () => {
    const nextIndex = (index + 1) % proverbs.length;
    setIndex(nextIndex);
    setCurrentProverb(proverbs[nextIndex]);
  };

  return (
    <div className={cn("fixed right-4 bottom-4 max-w-xs z-50", className)}>
      <button
        onClick={toggleWidget}
        className="bg-amber-600 hover:bg-amber-700 text-white p-3 rounded-full shadow-xl ml-auto flex transition-all duration-300 hover:scale-105"
        aria-label={isOpen ? "Close proverb widget" : "Open proverb widget"}
      >
        {isOpen ? "✕" : "🦜"}
      </button>
      
      {isOpen && (
        <div className="bg-white shadow-2xl rounded-lg mt-2 p-5 border-t-4 border-amber-600 animate-fade-in backdrop-blur-sm">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-slate-800 text-sm">Yoruba Wisdom</h3>
            <button 
              onClick={nextProverb} 
              className="text-amber-600 hover:text-amber-800 text-lg transition-colors duration-200 hover:scale-110"
              aria-label="Next proverb"
            >
              ↻
            </button>
          </div>
          
          <p className="text-slate-800 italic mb-2 text-sm leading-relaxed">
            "{currentProverb.yoruba}"
          </p>
          <p className="text-slate-600 text-xs leading-relaxed">
            {currentProverb.english}
          </p>
          
          <div className="flex justify-center mt-3 space-x-1">
            {proverbs.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  i === index ? "bg-amber-600" : "bg-slate-300"
                )}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProverbWidget;
